/**
 * The pose-layer projector — ADR-006 §3.3 (the scene is a VIEW) and ADR-007 §5
 * (the pose layer's semantics), made LIVE. `run-document.ts` applies the pose
 * layer once at load; this re-applies it every time the current Document
 * changes — a dispatch, an undo, a redo — WITHOUT re-importing the specimen.
 * That is the whole reason the substrate is cheap: undo is a cursor move over
 * frozen Documents, and the scene just re-projects the one the cursor now
 * points at.
 *
 * Undo-safe by construction: the projector snapshots each bone's rest rotation
 * the first time it is posed, and restores rest for any bone the current pose
 * layer no longer names (§5.4 — nothing re-asserts an undone pose because there
 * is no second substrate; the projection IS the Document's pose layer). A pose
 * naming an absent node refuses loudly, naming it (§8), never a silent skip.
 *
 * Engine-free: the scene is typed structurally (session-no-engine), the same
 * way the driver treats scenes as opaque. Depends on: nothing.
 * Depended on by: main (the loader re-projects on store publish).
 */

/** A local rotation the pose layer authored — the Document's `edits.poses[]`. */
export interface PoseLike {
  readonly bone: string;
  readonly rotation: { readonly x: number; readonly y: number; readonly z: number; readonly w: number };
}

/** The minimum a scene node must offer to be posed — three's Quaternion fits. */
export interface QuatNode {
  readonly quaternion: {
    readonly x: number;
    readonly y: number;
    readonly z: number;
    readonly w: number;
    set(x: number, y: number, z: number, w: number): unknown;
  };
}

/** The minimum a presented scene must offer — three's Object3D fits. */
export interface PoseScene {
  getObjectByName(name: string): QuatNode | null | undefined;
}

export interface PoseProjector {
  /**
   * Re-project the pose layer onto the live scene. Applies each pose, restores
   * rest for bones no longer posed (undo/clear), and returns the bones now
   * driven by the layer (evidence, never a silent write). Idempotent — the
   * current pose layer is the whole truth.
   */
  project(poses: readonly PoseLike[]): readonly string[];
  /**
   * Snapshot the CURRENT rotation of every named bone as its rest, skipping
   * bones whose rest is already known (seeded or captured). Call once at
   * creation, synchronously, BEFORE playback can sample — a lazy snapshot
   * taken mid-playback would record a mixer-sampled frame as "rest" (Codex
   * P2, #54 round 3). A named bone missing from the scene refuses loudly (§8).
   */
  captureRests(bones: readonly string[]): void;
}

/** A bone's imported rest rotation, known before any pose overwrote it. */
export interface SeedRest {
  readonly bone: string;
  readonly rotation: { readonly x: number; readonly y: number; readonly z: number; readonly w: number };
}

/**
 * Build a projector over a presented scene. `seedRests` supplies the imported
 * rest rotation of bones the driver ALREADY posed (a Document loaded with
 * poses) — without it, the projector would lazily snapshot the loaded pose AS
 * rest and "restore" to the pose on undo (Codex P2, #54). Bones posed live
 * (never pre-applied) are captured lazily and correctly at rest.
 */
export function createPoseProjector(scene: PoseScene, seedRests: readonly SeedRest[] = []): PoseProjector {
  const restRotations = new Map<string, { x: number; y: number; z: number; w: number }>();
  for (const { bone, rotation } of seedRests) {
    restRotations.set(bone, { x: rotation.x, y: rotation.y, z: rotation.z, w: rotation.w });
  }
  // The scene ALREADY holds these poses — the driver applied them at load, and
  // that is exactly what a seed rest records. Starting `posedLast` empty would
  // be a lie about the scene: project([]) would restore nothing, because the
  // projector would believe no bone is posed while the specimen visibly is
  // (Codex P2, #61 r6). Seeded here, the FIRST project([]) launders a loaded
  // pose back to imported rest — which is what lets a mixer be born onto rests
  // at boot, before any pose has been projected live.
  let posedLast = new Set<string>(seedRests.map(({ bone }) => bone));

  return {
    captureRests(bones) {
      for (const bone of bones) {
        if (restRotations.has(bone)) continue; // seeded truth wins (loaded poses)
        const node = scene.getObjectByName(bone);
        if (!node) {
          throw new Error(
            `captureRests names "${bone}" — no such node in the presented scene ` +
              `(§8: fails loudly, naming it; rest for a bone that does not exist is not a value).`,
          );
        }
        const { x, y, z, w } = node.quaternion;
        restRotations.set(bone, { x, y, z, w });
      }
    },
    project(poses) {
      const posedNow = new Set<string>();
      for (const pose of poses) {
        const node = scene.getObjectByName(pose.bone);
        if (!node) {
          throw new Error(
            `pose edit targets "${pose.bone}" — no such node in the presented scene ` +
              `(ADR-006 §8: a Document naming a bone that does not exist fails loudly, naming it).`,
          );
        }
        // Snapshot rest the first time a bone is posed — BEFORE overwriting —
        // so undo/clear can return it exactly (§5.4).
        if (!restRotations.has(pose.bone)) {
          const { x, y, z, w } = node.quaternion;
          restRotations.set(pose.bone, { x, y, z, w });
        }
        node.quaternion.set(pose.rotation.x, pose.rotation.y, pose.rotation.z, pose.rotation.w);
        posedNow.add(pose.bone);
      }
      for (const bone of posedLast) {
        if (posedNow.has(bone)) continue;
        const rest = restRotations.get(bone);
        const node = scene.getObjectByName(bone);
        if (rest && node) node.quaternion.set(rest.x, rest.y, rest.z, rest.w);
      }
      posedLast = posedNow;
      return [...posedNow];
    },
  };
}
